import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { apiClient } from '../providers/apiClient';

type Suite = 'participation' | 'jev' | 'quality';
interface Case { id: string; language: string; mode?: 'private' | 'shared' }
interface Catalog { datasetHash: string; routerPromptHash: string; participation: Case[]; quality: Case[] }
interface Result extends Case { suite: Suite; model?: string; actual?: boolean | null; expected?: boolean;
  error?: string | null; content?: string; review?: string; elapsedMs?: number; [key: string]: unknown }

/** Evaluation is manual and synthetic: never a background task and never account content. */
export function ChatEvaluationPanel() {
  const [catalog, setCatalog] = useState<Catalog>();
  const [results, setResults] = useState<Result[]>([]);
  const [running, setRunning] = useState<Suite>();
  const [error, setError] = useState(false);
  const [progress, setProgress] = useState('');
  const stop = useRef(false);
  useEffect(() => () => { stop.current = true; }, []);
  const run = async (suite: Suite) => {
    stop.current = false; setRunning(suite); setError(false);
    try {
      const cases = await apiClient.get<Catalog>('/admin/ai-config/chat-evaluation');
      setCatalog(cases);
      const selected = suite === 'quality' ? cases.quality : cases.participation;
      // Retain other suites so the comparison is exportable in one report.
      setResults(previous => previous.filter(r => r.suite !== suite));
      for (let index = 0; index < selected.length && !stop.current; index++) {
        const test = selected[index];
        setProgress(`${index + 1}/${selected.length} · ${test.id}${test.mode ? ' · ' + test.mode : ''}`);
        const result = await apiClient.post<Result>('/admin/ai-config/chat-evaluation',
          // ApiClient counts total attempts: one sends once, with no automatic retry.
          { suite, id: test.id, ...(test.mode ? { mode: test.mode } : {}) }, { timeout: 150_000, retries: 1 });
        setResults(previous => [...previous, result]);
      }
    } catch { setError(true); }
    finally { setRunning(undefined); setProgress(''); }
  };
  const exportReport = () => {
    const blob = new Blob([JSON.stringify({ catalog, results }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'rekindle-chat-evaluation.json'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return <Box mb={3}>
    <Typography variant="h6">Chat quality evaluation</Typography>
    <Typography variant="body2" color="text.secondary" mb={1}>
      Fixed synthetic conversations in five languages. Uses configured provider tokens, without sending account data or changing chats.
      Quality uses the Flame model, real prompts and runner, with simulated tools. JEV uses the configured Structured Decision model only for comparison; it does not change live routing.
    </Typography>
    <Box display="flex" gap={1} flexWrap="wrap">
      <Button variant="outlined" disabled={!!running} onClick={() => void run('participation')}>Evaluate participation</Button>
      <Button variant="outlined" disabled={!!running} onClick={() => void run('jev')}>Compare JEV</Button>
      <Button variant="outlined" disabled={!!running} onClick={() => void run('quality')}>Evaluate reply quality</Button>
      {running && <Button onClick={() => { stop.current = true; }}>Stop after this case</Button>}
      {!!results.length && <Button onClick={exportReport}>Download report</Button>}
    </Box>
    <Typography role="status">{running ? `${running} · ${progress}` : `${results.length} cases completed`}</Typography>
    {error && <Alert severity="error">Evaluation stopped. Completed results are retained. Check model configuration and connection before retrying.</Alert>}
    {(['participation', 'jev'] as const).map(suite => {
      const rows = results.filter(r => r.suite === suite);
      if (!rows.length) return null;
      const tp = rows.filter(r => r.actual === true && r.expected).length;
      const fp = rows.filter(r => r.actual === true && !r.expected).length;
      const positives = rows.filter(r => r.expected).length;
      const errors = rows.filter(r => r.actual == null || r.error).length;
      return <Typography key={suite} data-testid={`evaluation-${suite}-metrics`}>
        {suite}: {rows.length}/{catalog?.participation.length} cases · precision {tp + fp ? (100 * tp / (tp + fp)).toFixed(1) + '%' : 'not measurable'}
        {' · '}recall {positives ? (100 * tp / positives).toFixed(1) + '%' : 'not measurable'} · {errors} errors
      </Typography>;
    })}
    {!!results.length && <Alert severity="info">Partial runs cannot pass the release gate. Every quality response still needs a recorded human review; a successful API call is not a quality score.</Alert>}
    <Box aria-label="Chat evaluation results">
      {results.map((result, i) => <Box key={`${result.suite}-${result.id}-${result.mode}-${i}`} p={1} borderBottom={1} borderColor="divider">
        <Typography variant="subtitle2">{result.suite} · {result.id} · {result.mode} · {result.model}</Typography>
        {result.suite !== 'quality' && <Typography variant="body2">Expected: {String(result.expected)} · Actual: {String(result.actual)} · {result.elapsedMs} ms</Typography>}
        {result.error && <Alert severity="error">{result.error}</Alert>}
        {result.review && <Typography variant="caption">Review: {result.review}</Typography>}
        {result.content && <Typography sx={{ whiteSpace: 'pre-wrap' }}>{result.content}</Typography>}
      </Box>)}
    </Box>
  </Box>;
}
